import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';

import {
  IconBarChart,
  IconBell,
  IconBellOff,
  IconChat,
  IconCommand,
  IconDatabase,
  IconExternal,
  IconLogout,
  IconMoon,
  IconSun,
} from '@/icons';
import GlobalSearch from '@/components/GlobalSearch';
import { api } from '@/lib/api';
import { userInitials } from '@/lib/format';
import {
  requestNotificationPermission,
  setActiveConversation,
  setTitleSection,
  setUnreadTotal,
} from '@/lib/notify';
import { qk } from '@/lib/query';
import { isMiniApp, setBackButton } from '@/lib/telegram';
import type { ProjectAccess, SlaSummaryDto } from '@/lib/types';
import { useAuthStore, useIsPlatformAdmin } from '@/store/auth';
import { useUiStore } from '@/store/ui';

/**
 * Каркас панели: узкий левый рейл и остальное приложение справа.
 *
 * Разметка перенесена из старой панели (`.shell` / `.rail` / `.shell-main`) —
 * классы те же, что в index.css. Единственное значимое дополнение —
 * переключатель проектов сверху рейла: старая панель обслуживала один проект,
 * и места для него в дизайне просто не было. Паттерн взят из переключателя
 * аккаунтов Telegram, чтобы не изобретать новый.
 *
 * На мобиле рейл превращается в нижнюю навигацию (см. lib/app-shell.css).
 */

/** Опрос SLA. Тот же период, что у строки над списком диалогов. */
const SLA_POLL_MS = 30_000;

export default function AppShell(): ReactNode {
  const location = useLocation();
  const navigate = useNavigate();
  const setSearchOpen = useUiStore((state) => state.setSearchOpen);
  const searchOpen = useUiStore((state) => state.searchOpen);

  // Cmd/Ctrl+K — глобальный поиск. Каркас только поднимает флаг: сам диалог
  // рисует экран чата, у него под рукой список диалогов и переходы.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      } else if (event.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen, setSearchOpen]);

  // Заголовок вкладки: раздел рядом со счётчиком непрочитанных.
  useEffect(() => {
    setTitleSection(sectionTitle(location.pathname));
  }, [location.pathname]);

  /*
   * Какой диалог открыт — знает маршрут, и знать это должен ровно он.
   *
   * От этого зависит, промолчат ли уведомления: пинговать оператора о
   * сообщении в переписке, которую он сейчас читает, — верный способ заставить
   * его выключить звук насовсем.
   */
  useEffect(() => {
    const match = /^\/inbox\/([^/]+)/.exec(location.pathname);
    setActiveConversation(match?.[1] ?? null);
  }, [location.pathname]);

  /*
   * Кнопка «назад» Telegram вместо истории браузера.
   *
   * В webview кнопок браузера нет вовсе, а свайп назад закрывает Mini App
   * целиком — вместе с несохранённым черновиком. Показываем её везде, кроме
   * корня раздела, откуда возвращаться уже некуда.
   *
   * Из переписки уводим на список явно, а не шагом по истории: в диалог
   * попадают и по прямой ссылке, где записи для шага назад нет вовсе и
   * `navigate(-1)` вынес бы из Mini App наружу. То же решение — Chat.tsx:46.
   */
  useEffect(() => {
    if (!isMiniApp()) return;
    const atRoot = location.pathname === '/inbox' || location.pathname === '/';
    const inConversation = /^\/inbox\/[^/]+/.test(location.pathname);
    setBackButton(
      atRoot ? null : () => void (inConversation ? navigate('/inbox') : navigate(-1)),
    );
    return () => setBackButton(null);
  }, [location.pathname, navigate]);

  return (
    <div className="shell">
      <LeftRail />
      <div className="shell-main">
        <Outlet />
      </div>

      {/*
       * Глобальный поиск живёт в каркасе, а не на экране чата.
       *
       * Флаг `searchOpen` поднимает ⌘K и пункт меню — оба доступны на ЛЮБОМ
       * экране. Пока диалог рисовался внутри чата, на «Статистике» и в
       * админке нажатие ⌘K поднимало флаг, но рисовать его было некому:
       * поиск молча не открывался, а при следующем переходе в чат
       * распахивался сам собой — из-за флага, поднятого на другом экране.
       *
       * Компонент сам читает флаг и до открытия возвращает null, поэтому
       * монтирование здесь ничего не стоит. Важно, что мест монтирования
       * ровно одно: второе дало бы два диалога поверх друг друга.
       */}
      <GlobalSearch />
    </div>
  );
}

// ──────────────────────────── Рейл ────────────────────────────

function LeftRail(): ReactNode {
  const projects = useAuthStore((state) => state.projects);
  const currentProjectId = useAuthStore((state) => state.currentProjectId);
  const setCurrentProject = useAuthStore((state) => state.setCurrentProject);

  const awaiting = useProjectAwaiting(projects);

  // Счётчик во вкладке — сумма по всем проектам: свёрнутую вкладку оператор
  // видит одну на всю платформу, а не по проекту.
  const total = Object.values(awaiting).reduce((sum, value) => sum + value, 0);
  useEffect(() => setUnreadTotal(total), [total]);

  const currentAwaiting = currentProjectId ? (awaiting[currentProjectId] ?? 0) : 0;

  return (
    <nav className="rail" aria-label="Основная навигация">
      <div className="rail-top">
        {projects.length > 1 && (
          <>
            <ProjectSwitcher
              projects={projects}
              currentProjectId={currentProjectId}
              awaiting={awaiting}
              onPick={setCurrentProject}
            />
            <div className="rail-sep" />
          </>
        )}

        <RailLink to="/inbox" title="Чат" badge={currentAwaiting} icon={<IconChat size={22} />} />
        <RailLink to="/storage" title="Хранилище" icon={<IconDatabase size={22} />} />
        <RailLink to="/stats" title="Статистика" icon={<IconBarChart size={22} />} />
      </div>

      <div className="rail-bottom">
        <ThemeToggle />
        <RailUserMenu />
      </div>
    </nav>
  );
}

// ──────────────────────────── Переключатель проектов ────────────────────────────

/**
 * Плитки проектов над иконками разделов — единственное значимое дополнение к
 * перенесённому дизайну. Паттерн взят из переключателя аккаунтов Telegram:
 * соседний проект открывается одним нажатием, текущий раскрывает полный список.
 *
 * Список нужен не только для красоты. На мобиле рейл превращается в нижнюю
 * панель, где рядом с четырьмя разделами помещается ровно одна плитка (см.
 * app-shell.css), и без раскрывающегося списка переключиться было бы нельзя.
 * По той же причине он полезен и на десктопе, когда проектов больше пяти:
 * названия в плитку не влезают, а «AC» и «AB» на вид неразличимы.
 */
function ProjectSwitcher({
  projects,
  currentProjectId,
  awaiting,
  onPick,
}: {
  projects: ProjectAccess[];
  currentProjectId: string | null;
  awaiting: Record<string, number>;
  onPick: (projectId: string) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const wrapRef = useDismissible(open, () => setOpen(false));

  return (
    <div className="dd-wrap rail-projects-wrap" ref={wrapRef}>
      <div className="rail-projects" role="tablist" aria-label="Проекты">
        {projects.map((project) => {
          const active = project.id === currentProjectId;
          const count = awaiting[project.id] ?? 0;
          return (
            <button
              key={project.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={['rail-project', active ? 'active' : '', project.isActive ? '' : 'off']
                .filter(Boolean)
                .join(' ')}
              title={project.isActive ? project.name : `${project.name} (отключён)`}
              // Нажатие на текущий проект раскрывает список, на чужой —
              // переключает сразу: лишнее подтверждение на каждое переключение
              // раздражало бы куда сильнее, чем экономило ошибки.
              onClick={() => (active ? setOpen((value) => !value) : onPick(project.id))}
            >
              <span>{projectMark(project)}</span>
              {count ? <span className="rail-badge">{badgeText(count)}</span> : null}
            </button>
          );
        })}
      </div>

      {open && (
        <div className="dd-menu rail-menu" role="menu">
          <div className="dd-head">
            <div className="dd-userblock">Проекты</div>
          </div>
          {projects.map((project) => {
            const active = project.id === currentProjectId;
            const count = awaiting[project.id] ?? 0;
            return (
              <button
                key={project.id}
                type="button"
                role="menuitem"
                className={`dd-item ${active ? 'active' : ''}`}
                onClick={() => {
                  onPick(project.id);
                  setOpen(false);
                }}
              >
                <span className="dd-ico">{projectMark(project)}</span>
                <span className="dd-label">
                  {project.name}
                  {!project.isActive && ' · отключён'}
                </span>
                {count ? <span className="dd-trail">{badgeText(count)}</span> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Метка проекта на плитке.
 *
 * Код проекта короткий и осмысленный (`acme`, `bet-tr`) — берём две первых
 * буквы от него, а не от названия: названия у соседних проектов часто
 * начинаются одинаково («Казино Турция», «Казино Бразилия»).
 */
function projectMark(project: ProjectAccess): string {
  const source = project.code || project.name;
  return source.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 2).toUpperCase() || '??';
}

function badgeText(value: number): string {
  return value > 99 ? '99+' : String(value);
}

/**
 * Закрыть всплывающее меню по клику мимо и по Escape.
 *
 * Слушатели вешаются только пока меню открыто: держать их постоянно — это
 * обработчик mousedown на каждый клик в переписке, а их там много.
 * mousedown, а не click: иначе нажатие на кнопку внутри меню успевает
 * закрыть его до того, как отработает собственный onClick.
 */
function useDismissible(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) closeRef.current();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return ref;
}

function RailLink({
  to,
  title,
  icon,
  badge,
}: {
  to: string;
  title: string;
  icon: ReactNode;
  badge?: number;
}): ReactNode {
  return (
    <NavLink
      to={to}
      // Активность по префиксу: /inbox/:id и /storage/tags обязаны подсвечивать
      // свой раздел, иначе оператор теряет, где он находится.
      className={({ isActive }) => `rail-btn ${isActive ? 'active' : ''}`}
      title={title}
      aria-label={title}
    >
      <span className="rail-ico">{icon}</span>
      {badge ? <span className="rail-badge">{badgeText(badge)}</span> : null}
    </NavLink>
  );
}

function ThemeToggle(): ReactNode {
  const theme = useUiStore((state) => state.theme);
  const toggleTheme = useUiStore((state) => state.toggleTheme);
  const dark = theme === 'dark';

  return (
    <button
      type="button"
      className="rail-btn"
      title={dark ? 'Светлая тема' : 'Тёмная тема'}
      aria-label={dark ? 'Светлая тема' : 'Тёмная тема'}
      onClick={toggleTheme}
    >
      <span className="rail-ico">{dark ? <IconSun size={20} /> : <IconMoon size={20} />}</span>
    </button>
  );
}

// ──────────────────────────── Меню оператора ────────────────────────────

function RailUserMenu(): ReactNode {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const isAdmin = useIsPlatformAdmin();
  const sound = useUiStore((state) => state.soundEnabled);
  const setSound = useUiStore((state) => state.setSound);
  const setSearchOpen = useUiStore((state) => state.setSearchOpen);

  const [open, setOpen] = useState(false);
  const wrapRef = useDismissible(open, () => setOpen(false));

  if (!user) return null;

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    // Разрешение спрашиваем здесь, а не при загрузке: Chrome игнорирует запрос
    // вне жеста пользователя и второго шанса спросить уже не даёт.
    if (next) void requestNotificationPermission();
  };

  return (
    <div className="dd-wrap" ref={wrapRef}>
      <button
        type="button"
        className="rail-avatar"
        title="Меню"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        <span>{userInitials(user.name || user.email)}</span>
        <span className="rail-presence" />
      </button>

      {open && (
        <div className="dd-menu top rail-user-menu" role="menu">
          <div className="dd-head">
            <div className="dd-userblock">
              <div style={{ color: 'var(--text)', fontWeight: 600 }}>{user.name || user.email}</div>
              <div className="dd-username">{user.email}</div>
            </div>
          </div>

          <button type="button" role="menuitem" className="dd-item" onClick={toggleSound}>
            <span className="dd-ico">
              {sound ? <IconBell size={16} /> : <IconBellOff size={16} />}
            </span>
            <span className="dd-label">Уведомления</span>
            <span className="dd-trail">
              <span className={`switch ${sound ? 'on' : ''}`} aria-hidden>
                <span className="switch-knob" />
              </span>
            </span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="dd-item"
            onClick={() => {
              setOpen(false);
              setSearchOpen(true);
            }}
          >
            <span className="dd-ico">
              <IconCommand size={16} />
            </span>
            <span className="dd-label">Глобальный поиск</span>
            <span className="dd-trail">
              <kbd>⌘K</kbd>
            </span>
          </button>

          {isAdmin && (
            <>
              <div className="dd-sep" role="separator" />
              <button
                type="button"
                role="menuitem"
                className="dd-item"
                onClick={() => {
                  setOpen(false);
                  void navigate('/admin');
                }}
              >
                <span className="dd-ico">
                  <IconExternal size={16} />
                </span>
                <span className="dd-label">Администрирование</span>
              </button>
            </>
          )}

          <div className="dd-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="dd-item danger"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            <span className="dd-ico">
              <IconLogout size={16} />
            </span>
            <span className="dd-label">Выйти</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── Счётчики проектов ────────────────────────────

/**
 * Сколько клиентов ждут ответа в каждом проекте.
 *
 * Берём ту же сводку, что рисует строку SLA над списком (`awaiting`), а не
 * сумму непрочитанных: оператору важно число ЛЮДЕЙ, которые ждут, а не число
 * сообщений — десять реплик от одного клиента это одна задача, а не десять.
 *
 * Запрос на проект, а не один общий: маршрут SLA живёт под проектом и
 * проверяет membership по нему. Проектов у оператора единицы.
 */
function useProjectAwaiting(projects: ProjectAccess[]): Record<string, number> {
  const results = useQueries({
    queries: projects.map((project) => ({
      queryKey: qk.sla(project.id),
      queryFn: () => api.get<SlaSummaryDto>(`/projects/${project.id}/sla`),
      refetchInterval: SLA_POLL_MS,
      // Значение живёт ровно до следующего опроса: показывать вчерашнее число
      // ждущих хуже, чем не показывать никакого.
      staleTime: SLA_POLL_MS,
      enabled: project.isActive,
    })),
  });

  // Без useMemo намеренно: размер массива зависимостей менялся бы вместе с
  // числом проектов, а это прямое нарушение правил хуков. Свёртка десятка
  // чисел на рендер дешевле любой мемоизации вокруг неё.
  const map: Record<string, number> = {};
  results.forEach((result, index) => {
    const project = projects[index];
    if (project && result.data) map[project.id] = result.data.awaiting;
  });
  return map;
}

/** Раздел в заголовке вкладки. */
function sectionTitle(pathname: string): string {
  if (pathname.startsWith('/storage')) return 'Хранилище';
  if (pathname.startsWith('/stats')) return 'Статистика';
  if (pathname.startsWith('/admin')) return 'Администрирование';
  return 'Чат';
}
